import React, { FC, useEffect, useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useForm } from 'react-hook-form'
import moment from 'moment'
import { Button } from '../../ui/Button/Button'
import { Typography } from '../../ui/Typography/Typography'
import { colors } from '../../../theme/palette'
import { FormTextArea } from '../FormElements/FormInput'
import { FormControl } from '../../ui/FormElememts/FormControl'
import { baseButton } from '../../../theme/reuseables'
import { updateNote } from '../../../api/notes'
import { Note, UpdateNoteType } from '../../../models/Note'
import { L } from '../../providers/Lokalise'
import Input from '../../ui/FormElememts/Input'

interface EditProps {
    note: Note
    onUpdated?: (note: Note) => void
    onCancel?: () => void
}

const useStyles = createUseStyles({
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: 'calc(100% - 100px)',
        paddingBottom: '80px',
    },
    accordionWrapper: {
        marginBottom: '20px',
    },
    select: {
        position: 'relative',
        height: '3.125rem',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.labelColor,
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
    date: {
        position: 'relative',
        marginTop: '20px',
    },
    datePlaceholder: {
        position: 'absolute',
        top: '-38px',
        zIndex: '1',
    },
})

export type RegistrationFormFields = {
    Titel: string
    Indhold: string
    ReferenceType: string
}

export const EditNote: FC<EditProps> = ({ note, onUpdated, onCancel }) => {
    const { register, handleSubmit, reset, setValue } =
        useForm<RegistrationFormFields>()
    const classes = useStyles()
    const inputReference = useRef(null)

    useEffect(() => {
        if (inputReference && inputReference.current) {
            setTimeout(() => {
                inputReference.current.focus()
            }, 500)
        }
    }, [note])

    const resetForm = (): void => {
        reset()
    }

    const onSubmit = async (data: RegistrationFormFields): Promise<void> => {
        const fieldsToEdit: UpdateNoteType = {
            NoteId: note.id,
        }

        fieldsToEdit.Indhold = data.Indhold || undefined
        fieldsToEdit.Titel = data.Titel || note.title

        const response = await updateNote(fieldsToEdit)

        if (response) {
            if (onUpdated) {
                onUpdated(note)
            }
        }
    }

    return (
        <>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className={classes.contentWrapper}
            >
                <Typography
                    tag="h2"
                    size="XL"
                    align="center"
                    weight="bold"
                    style={{ margin: '20px 0 10px' }}
                >
                    {L('NOTER_REDIGER-NOTE_FORM_TITEL')}
                </Typography>

                <FormControl>
                    <Input
                        label={L('NOTER_REDIGER_FORM_NOTE-OVERSKRIFT_LABEL')}
                        required
                        defaultValue={note.title}
                        autoFocus
                        ref={inputReference}
                        onChange={(e) => {
                            setValue('Titel', e.target.value)
                        }}
                    />
                </FormControl>

                <FormControl>
                    <FormTextArea
                        label={L('NOTER_REDIGER_FORM_NOTE-INDHOLD_LABEL')}
                        register={register}
                        name="Indhold"
                        required={false}
                        maxCount={3000}
                        defaultValue={note.content}
                    />
                </FormControl>
                <div className={classes.date}>
                    <span className={classes.datePlaceholder}>
                        <Typography size="XXS" color={colors.labelColor}>
                            Sidst ændret{' '}
                            {`${moment(note.updated).format(
                                'DD.MM.YYYY'
                            )}, kl.${moment(note.updated).format('HH:mm')}`}
                        </Typography>
                    </span>
                </div>

                <div className={classes.bottom}>
                    <Button
                        onClick={() => {
                            if (onCancel) {
                                onCancel()
                            }
                            resetForm()
                        }}
                        type="secondary"
                        size="large"
                    >
                        {L('NOTER_REDIGER_FORM_ANNULLER_KNAP')}
                    </Button>
                    <Button buttonType="submit" type="primary" size="large">
                        {L('NOTER_REDIGER_FORM_GEM_KNAP')}
                    </Button>
                </div>

                <div />
            </form>
        </>
    )
}
